import { IconButton, TextField } from "@material-ui/core";
import { StatContext } from 'context/StatContext';
import { compile, parse } from "mathjs";
import Stat from "models/Stat";
import { useContext, useMemo, useRef, useState } from "react";
import './StatColumn.scoped.scss';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { Close } from "@material-ui/icons";
import { ServiceLocatorContext } from "context/ServiceLocatorContext";

interface StatColumnProps {
    children?: React.ReactNode;
    stat: Stat;
    className?: string;
}

export default function StatColumn(props: StatColumnProps) {
    const { stat } = props;
    const context = useContext(StatContext);
    const [formula, setFormula] = useState(stat.formula);
    const [isNameFocused, setNameFocused] = useState(false);
    const compiledFormula = useRef(compile(stat.formula.toLowerCase()));
    const compiledData = useRef([]);
    const nameRef = useRef(null);
    const { statComputationService } = useContext(ServiceLocatorContext);

    const updateStatValue = (value) => {
        const lowerValue = value.toLowerCase();
        try {
            const newCompiled = compile(lowerValue);

            // Make sure it works on at least the first row before re-render.
            statComputationService.computeStat(stat, stat.minIndex, {}, lowerValue, newCompiled);

            if (lowerValue.trim() !== stat.formula.toLowerCase()) {
                compiledFormula.current = newCompiled;
                stat.formula = value;
                context.updateStat(stat);
            }
        } catch (e) {
        }
        setFormula(value);
    };

    /**
     * Update a specific cell value
     * @param index 
     * @param value 
     */
    const updateCellValue = (index: number, value: string) => {
        if (value.trim() === '') {
            stat.removeValue(index);
            context.updateStat(stat);
            return;
        }
        if (isNaN(parseFloat(value))) {
            return;
        }
        stat.setValue(index, parseFloat(value));
        context.updateStat(stat);
    }

    const updateStatName = (newName) => {
        if (newName === stat.name) {
            return;
        }

        stat.name = newName;
        context.updateStat(stat);
    }

    const blurNameField = (event) => {
        nameRef?.current.blur();
        event.preventDefault();
    };

    /**
     * Memoized computation of the formula, returning elements with the values in them.
     */
    const valueCells = useMemo(() => {
        let elements = [];
        let cache = {};
        compiledData.current = [];

        for (let i = stat.minIndex; i <= stat.maxIndex; i++) {
            let value = stat.hasValue(i) ? stat.getValue(i) : 0;
            if (!stat.getValue(i)) {
                try {
                    value = statComputationService.computeStat(stat, i, cache);
                } catch (e) {
                    value = 0;
                }
            }
            const classes = 'row-input ' + (stat.hasValue(i) ? 'custom-value' : '');
            compiledData.current.push(value);

            elements.push(
                (
                    <div key={i} className="flex-row row">
                        <b className="row-index">{i}</b>
                        <TextField
                            className={classes}
                            value={value}
                            onChange={(e) => updateCellValue(i, e.target.value)}
                        ></TextField>
                    </div>));
        }
        return elements;
    }, [stat.formula, stat.minIndex, stat.maxIndex, stat.valueOverrides, statComputationService.getFormulaDeps(stat)]);

    /**
     * Memoized computation of the Sparkline graph to avoid useless re-renders.
     */
    const sparklines = useMemo(() => {
        return <Sparklines data={compiledData.current} height={100}>
            <SparklinesLine color="orange" />
        </Sparklines>;
    }, [compiledData.current]);

    return (
        <div className={"card flex-col " + props.className}>
            <div className="header flex-row">
                <textarea
                    ref={nameRef}
                    value={stat.name ?? ''}
                    rows={1}
                    className={"stat-name " + (isNameFocused ? 'focused' : '')}
                    wrap="nowrap"
                    onFocus={e => setNameFocused(true)}
                    onBlur={e => setNameFocused(false)}
                    onChange={e => updateStatName(e.currentTarget.value)}
                    onKeyDown={e => e.key === 'Enter' ? blurNameField(e) : null}
                    suppressContentEditableWarning>
                </textarea>
                <IconButton color="secondary"
                    size="small"
                    onClick={() => context.removeStat(stat.id)}>
                    <Close fontSize="small"></Close>
                </IconButton>
            </div>
            <div className="formula-field">
                <TextField
                    label="Formula"
                    value={formula}
                    onChange={(e) => updateStatValue(e.target.value)}
                ></TextField>
            </div>
            <div className="sparklines">
                {sparklines}
            </div>
            {valueCells}
        </div >
    );
}